/**
 * useLgpdAnonymization — hooks para Anonymization LGPD (Brief 5 B5 Wizard).
 *
 * Sprint Frontend Desktop W3.2. Consome endpoints W2.2 lgpd/ módulo (PR #65).
 *
 * Capabilities server-side:
 *   - lgpd-requests-read (Team+) — listagem
 *   - lgpd-anonymization-execute (DPO exclusivo) — criar + executar
 *
 * Decisão executiva: useCreateAndExecuteAnonymization combina create + execute
 * em sequência para o Wizard Step 4 (irreversível). Sem optimistic update —
 * P15 (Privacidade desde o desenho): ação irreversível espera confirmação server.
 *
 * ADRs: ADR-024 + ADR-098 + ADR-110 (3 níveis: partial / full / delete_cascade).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api-client';

import type {
  AnonymizationLevel,
  AnonymizationWireItem,
} from '../features/dpo/centro-direitos/types';

const QUERY_KEY = 'lgpd-anonymizations';
const STALE_TIME = 30_000;

export function useAnonymizationsByRequest(requestId: string | null): {
  data: AnonymizationWireItem[];
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: [QUERY_KEY, 'by-request', requestId],
    queryFn: async () => {
      if (requestId === null) throw new Error('requestId required');
      const res = await api.listLgpdAnonymizationsByRequest(requestId);
      return res.data;
    },
    enabled: requestId !== null,
    staleTime: STALE_TIME,
    retry: 1,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/**
 * Wizard Step 4 — ação irreversível: create anonymization → execute.
 * Modal destructive com typing-to-confirm confirma antes de chamar.
 *
 * P15: defaults conservadores (level 'partial' forced by Wizard Step 2).
 * P17: audit emitido server-side na execução.
 */
export function useInitiateAnonymization(): ReturnType<
  typeof useMutation<
    { anonymizationId: string; status: string },
    Error,
    {
      entityType: string;
      entityId: string;
      level: AnonymizationLevel;
      lgpdRequestId?: string;
    }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ entityType, entityId, level, lgpdRequestId }) => {
      // Step 1: create
      const created = await api.createAnonymization({
        entityType,
        entityId,
        level,
        ...(lgpdRequestId !== undefined ? { lgpdRequestId } : {}),
      });
      // Step 2: execute (irreversível — somente após Modal destructive confirm)
      const executed = await api.executeAnonymization(created.anonymizationId);
      return { anonymizationId: created.anonymizationId, status: executed.status };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      void qc.invalidateQueries({ queryKey: ['lgpd-requests'] });
    },
  });
}
